import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Send, Users, Search, CheckSquare, Square, Eye, X, Info, Clock, Calendar, Trash2, Bot, FileDown, ChevronDown, ChevronUp, BarChart3, FolderOpen, ImagePlus, Link as LinkIcon } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  confirmed: { label: "Confirmed", color: "bg-green-100 text-green-800 border-green-200", dot: "bg-green-500" },
  declined: { label: "Declined", color: "bg-red-100 text-red-800 border-red-200", dot: "bg-red-500" },
  stop: { label: "Stop / Opt-Out", color: "bg-slate-100 text-slate-800 border-slate-200", dot: "bg-slate-500" },
  question: { label: "Question", color: "bg-blue-100 text-blue-800 border-blue-200", dot: "bg-blue-500" },
  uncategorized: { label: "Uncategorized", color: "bg-amber-100 text-amber-800 border-amber-200", dot: "bg-amber-500" },
};

function BlastReplyReport({ blastId, onClose }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("confirmed");
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/api/blasts/${blastId}/replies`);
        setReport(r.data);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, [blastId]);

  const downloadCSV = () => {
    if (!report) return;
    const rows = [["Category", "First Name", "Last Name", "Phone", "Email", "Stage", "Reply", "Confidence", "AI Summary", "Auto-Replied", "Replied At"]];
    Object.entries(report.replies).forEach(([cat, entries]) => {
      entries.forEach(e => {
        rows.push([cat, e.first_name, e.last_name, e.phone, e.email, e.stage, `"${(e.reply_body || "").replace(/"/g, '""')}"`, e.confidence, `"${(e.summary || "").replace(/"/g, '""')}"`, e.auto_replied ? "Yes" : "No", e.replied_at]);
      });
    });
    const csv = rows.map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `blast-replies-${blastId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
      <div className="card p-8 max-w-lg w-full mx-4 text-center">
        <div className="w-6 h-6 border-2 border-[#FF5C35] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-sm text-slate-600">Loading reply report...</p>
      </div>
    </div>
  );

  if (!report) return null;

  const tabs = Object.keys(CATEGORY_META);
  const activeEntries = report.replies[activeTab] || [];

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col" data-testid="blast-report-modal">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-lg font-semibold font-heading text-slate-900 flex items-center gap-2">
              <BarChart3 size={18} className="text-[#FF5C35]" /> AI Reply Report
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">{report.total_replies} total replies · Blast sent to {report.blast_body ? `"${report.blast_body.slice(0, 50)}..."` : "contacts"}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={downloadCSV} className="btn-secondary py-1.5 px-3 text-xs" data-testid="download-csv-btn">
              <FileDown size={13} /> Export CSV
            </button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="px-6 py-3 grid grid-cols-5 gap-2 border-b border-slate-100 shrink-0">
          {tabs.map(cat => {
            const meta = CATEGORY_META[cat];
            const count = report.summary[cat] || 0;
            return (
              <button
                key={cat}
                onClick={() => setActiveTab(cat)}
                className={`rounded-lg p-2 text-center transition-all border ${activeTab === cat ? "ring-2 ring-[#FF5C35] ring-offset-1" : ""} ${meta.color}`}
              >
                <p className="text-lg font-bold">{count}</p>
                <p className="text-[10px] font-medium uppercase tracking-wide">{meta.label}</p>
              </button>
            );
          })}
        </div>

        {/* Entries */}
        <div className="flex-1 overflow-y-auto px-6 py-3">
          {activeEntries.length === 0 ? (
            <div className="text-center py-10 text-slate-400 text-sm">No replies in this category</div>
          ) : (
            <div className="space-y-2">
              {activeEntries.map((e, i) => (
                <div key={i} className="border border-slate-200 rounded-lg overflow-hidden">
                  <div
                    className="flex items-center justify-between px-4 py-2.5 cursor-pointer hover:bg-slate-50"
                    onClick={() => setExpanded(x => ({ ...x, [i]: !x[i] }))}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full shrink-0 ${CATEGORY_META[activeTab].dot}`} />
                      <div>
                        <p className="text-sm font-medium text-slate-900">{e.first_name} {e.last_name}</p>
                        <p className="text-xs text-slate-500">{e.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {e.auto_replied && (
                        <span className="text-[10px] font-semibold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Auto-replied</span>
                      )}
                      <span className="text-xs text-slate-400">{e.confidence ? `${Math.round(e.confidence * 100)}%` : ""}</span>
                      {expanded[i] ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
                    </div>
                  </div>
                  {expanded[i] && (
                    <div className="px-4 py-3 bg-slate-50 border-t border-slate-100 space-y-2">
                      <div>
                        <p className="text-[10px] uppercase font-semibold text-slate-400 mb-0.5">Their Reply</p>
                        <p className="text-sm text-slate-800 bg-white px-3 py-2 rounded border border-slate-200">{e.reply_body}</p>
                      </div>
                      {e.summary && (
                        <div>
                          <p className="text-[10px] uppercase font-semibold text-slate-400 mb-0.5">AI Reasoning</p>
                          <p className="text-xs text-slate-600">{e.summary}</p>
                        </div>
                      )}
                      <div className="flex gap-4 text-xs text-slate-500">
                        {e.email && <span>Email: {e.email}</span>}
                        {e.stage && <span>Stage: {e.stage}</span>}
                        {e.replied_at && <span>Replied: {new Date(e.replied_at).toLocaleString()}</span>}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const MERGE_FIELDS = ["{first_name}", "{last_name}", "{agent_name}", "{address}"];
const SMS_MAX = 320;
const URL_PATTERN = /https?:\/\/|www\.|\.com\/|\.net\/|\.org\//i;

function SmsIndicator({ text }) {
  const len = text.length;
  const hasUrl = URL_PATTERN.test(text);
  const segments = len <= 160 ? 1 : Math.ceil(len / 153);
  const overLimit = len > SMS_MAX;
  const color = overLimit ? "text-red-600" : len > 250 ? "text-amber-600" : "text-slate-400";
  return (
    <div className="space-y-1 mt-1.5">
      <div className="flex items-center justify-between">
        <span className={`text-xs font-medium ${color}`}>
          {len}/{SMS_MAX} chars · {segments} segment{segments !== 1 ? "s" : ""}
        </span>
        <div className="flex items-center gap-1 text-xs text-slate-400">
          <Info size={11} />
          "Reply STOP to unsubscribe" appended automatically
        </div>
      </div>
      {/* Progress bar */}
      <div className="h-1 bg-slate-200 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${overLimit ? "bg-red-500" : len > 250 ? "bg-amber-500" : "bg-[#FF5C35]"}`}
          style={{ width: `${Math.min((len / SMS_MAX) * 100, 100)}%` }}
        />
      </div>
      {overLimit && (
        <p className="text-xs text-red-600 flex items-center gap-1">
          <Info size={11} /> Message too long — carriers will block delivery. Remove signatures and keep it short.
        </p>
      )}
      {hasUrl && (
        <p className="text-xs text-red-600 flex items-center gap-1">
          <Info size={11} /> URLs are not allowed in blasts — carriers flag them as spam.
        </p>
      )}
      {segments > 2 && !overLimit && (
        <p className="text-xs text-amber-600 flex items-center gap-1">
          <Info size={11} /> {segments} segments increases cost and carrier filtering risk. Try to keep under 160 chars.
        </p>
      )}
    </div>
  );
}

function ScheduledBlastsList({ blasts, onCancel, onRefresh }) {
  if (!blasts || blasts.length === 0) return null;

  return (
    <div className="card overflow-hidden mb-5" data-testid="scheduled-blasts">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-amber-50">
        <div className="flex items-center gap-2">
          <Clock size={14} className="text-amber-600" />
          <span className="text-sm font-semibold text-amber-800">Scheduled Blasts</span>
          <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full font-medium">{blasts.length}</span>
        </div>
      </div>
      <div className="divide-y divide-slate-100">
        {blasts.map(b => {
          const scheduledDate = new Date(b.scheduled_at);
          return (
            <div key={b.id} className="px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-800 truncate font-mono">{b.body}</p>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-xs text-slate-500 flex items-center gap-1">
                    <Calendar size={10} />
                    {scheduledDate.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                  </span>
                  <span className="text-xs text-slate-500 flex items-center gap-1">
                    <Clock size={10} />
                    {scheduledDate.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                  </span>
                  <span className="text-xs text-slate-500 flex items-center gap-1">
                    <Users size={10} />
                    {b.total} recipients
                  </span>
                </div>
              </div>
              <button
                onClick={() => onCancel(b.id)}
                className="ml-3 p-2 text-red-500 hover:bg-red-50 rounded-md transition-colors shrink-0"
                title="Cancel scheduled blast"
                data-testid={`cancel-blast-${b.id}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Blast() {
  const [contacts, setContacts] = useState([]);
  const [allContacts, setAllContacts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [body, setBody] = useState("");
  const [search, setSearch] = useState("");
  const [filterStage, setFilterStage] = useState("");
  const [filterTag, setFilterTag] = useState("");
  const [stages, setStages] = useState([]);
  const [tags, setTags] = useState([]);
  const [preview, setPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  // Scheduling state
  const [scheduleMode, setScheduleMode] = useState(false);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [scheduledBlasts, setScheduledBlasts] = useState([]);
  // Smart List state
  const [audienceMode, setAudienceMode] = useState("synced"); // "synced", "smartlist", or "group"
  const [smartLists, setSmartLists] = useState([]);
  const [selectedList, setSelectedList] = useState("");
  const [loadingSmartList, setLoadingSmartList] = useState(false);
  const [totalInFub, setTotalInFub] = useState(null);
  // Groups state
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [groupMembers, setGroupMembers] = useState([]);
  const [selectedGroupMembers, setSelectedGroupMembers] = useState([]);
  const [loadingGroup, setLoadingGroup] = useState(false);
  // AI Auto-Reply state
  const [autoReplyEnabled, setAutoReplyEnabled] = useState(false);
  const [autoReplyMessage, setAutoReplyMessage] = useState("Thanks {first_name}! Great to hear from you. Someone from our team will reach out to you shortly.");
  const [reportBlastId, setReportBlastId] = useState(null);
  // MMS state
  const [blastImage, setBlastImage] = useState(null);
  const [blastUploading, setBlastUploading] = useState(false);
  const [showBlastUrlInput, setShowBlastUrlInput] = useState(false);
  const [blastImageUrlInput, setBlastImageUrlInput] = useState("");
  const blastFileRef = useRef(null);

  const handleBlastFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowed.includes(file.type)) { alert("Only JPEG, PNG, GIF, or WebP allowed"); return; }
    if (file.size > 5 * 1024 * 1024) { alert("Image must be under 5 MB"); return; }
    setBlastUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await axios.post(`${API}/api/upload/image`, form);
      setBlastImage({ url: r.data.url, filename: r.data.filename, preview: URL.createObjectURL(file) });
    } catch (e) { alert(e.response?.data?.detail || "Upload failed"); }
    setBlastUploading(false);
    if (blastFileRef.current) blastFileRef.current.value = "";
  };

  const handleBlastUrlUpload = async () => {
    const url = blastImageUrlInput.trim();
    if (!url) return;
    setBlastUploading(true);
    try {
      const r = await axios.post(`${API}/api/upload/image-url`, { url });
      setBlastImage({ url: r.data.url, filename: r.data.filename, preview: r.data.url });
      setBlastImageUrlInput("");
      setShowBlastUrlInput(false);
    } catch (e) { alert(e.response?.data?.detail || "URL upload failed"); }
    setBlastUploading(false);
  };

  const removeBlastImage = () => {
    if (blastImage?.preview?.startsWith("blob:")) URL.revokeObjectURL(blastImage.preview);
    setBlastImage(null);
  };

  const load = async (searchOverride) => {
    setLoading(true);
    try {
      const s = searchOverride !== undefined ? searchOverride : search;
      const params = { limit: 0, ...(s && { search: s }), ...(filterStage && { stage: filterStage }), ...(filterTag && { tag: filterTag }) };
      const cr = await axios.get(`${API}/api/contacts`, { params });
      const active = cr.data.contacts.filter(c => !c.opted_out);
      setContacts(active);
      setAllContacts(active);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  // Load stats once on mount
  useEffect(() => {
    const loadStats = async () => {
      try {
        const sr = await axios.get(`${API}/api/contacts/stats`);
        setStages((sr.data.stages || []).map(s => s.stage));
        setTags((sr.data.top_tags || []).map(t => t.tag));
      } catch (e) { console.error(e); }
    };
    loadStats();
  }, []);

  const loadScheduled = async () => {
    try {
      const r = await axios.get(`${API}/api/blasts/scheduled`);
      setScheduledBlasts(r.data);
    } catch (e) { console.error(e); }
  };

  const [ponds, setPonds] = useState([]);

  const loadSmartListsAndPonds = async () => {
    try {
      const [slr, pr] = await Promise.all([
        axios.get(`${API}/api/fub/smart-lists`),
        axios.get(`${API}/api/fub/ponds`)
      ]);
      setSmartLists(slr.data.smart_lists || []);
      setPonds(pr.data.ponds || []);
    } catch (e) { console.error(e); }
  };

  const loadListContacts = async () => {
    if (!selectedList) return;
    setLoadingSmartList(true);
    try {
      let url;
      if (selectedList.startsWith("pond:")) {
        url = `${API}/api/fub/ponds/${selectedList.replace("pond:", "")}/contacts`;
      } else {
        url = `${API}/api/fub/smart-lists/${selectedList.replace("smartlist:", "")}/contacts`;
      }
      const r = await axios.get(url);
      const loaded = r.data.contacts || [];
      setContacts(loaded);
      setAllContacts(loaded);
      setSelected(loaded.map(c => c.id));
      setTotalInFub(r.data.total_in_fub || null);
    } catch (e) { console.error(e); }
    setLoadingSmartList(false);
  };

  const loadGroups = async () => {
    try {
      const r = await axios.get(`${API}/api/groups`);
      setGroups(r.data);
    } catch (e) { console.error(e); }
  };

  const loadGroupMembers = async () => {
    if (!selectedGroupId) return;
    setLoadingGroup(true);
    try {
      const r = await axios.get(`${API}/api/groups/${selectedGroupId}/members`);
      setGroupMembers(r.data);
      setSelectedGroupMembers(r.data.map(m => m.id));
    } catch (e) { console.error(e); }
    setLoadingGroup(false);
  };

  useEffect(() => { loadSmartListsAndPonds(); loadGroups(); }, []);
  useEffect(() => { if (audienceMode === "synced") { load(); } loadScheduled(); }, [filterStage, filterTag, audienceMode]);
  useEffect(() => {
    if (audienceMode !== "synced") return;
    const t = setTimeout(() => load(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const toggleSelect = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const selectAll = () => setSelected(contacts.map(c => c.id));
  const clearAll = () => setSelected([]);
  const toggleGroupMember = (id) => setSelectedGroupMembers(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const selectAllGroupMembers = () => setSelectedGroupMembers(groupMembers.map(m => m.id));
  const clearAllGroupMembers = () => setSelectedGroupMembers([]);

  const previewMsg = (contact) => {
    if (!contact) return body;
    return body
      .replace("{first_name}", contact.first_name || "")
      .replace("{last_name}", contact.last_name || "")
      .replace("{agent_name}", "Your Agent")
      .replace("{address}", contact.address || "the property");
  };

  const cancelScheduled = async (blastId) => {
    if (!window.confirm("Cancel this scheduled blast?")) return;
    try {
      await axios.delete(`${API}/api/blasts/${blastId}`);
      loadScheduled();
    } catch (e) {
      alert(e.response?.data?.detail || "Cancel failed");
    }
  };

  // Get default schedule: tomorrow at 10:00 AM
  const getDefaultSchedule = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return {
      date: tomorrow.toISOString().split("T")[0],
      time: "10:00"
    };
  };

  const toggleScheduleMode = () => {
    if (!scheduleMode) {
      const defaults = getDefaultSchedule();
      setScheduleDate(defaults.date);
      setScheduleTime(defaults.time);
    }
    setScheduleMode(!scheduleMode);
  };

  const [blastProgress, setBlastProgress] = useState(null);

  const sendBlast = async () => {
    if (!body.trim()) { alert("Please write a message"); return; }

    // Group mode — uses group blast endpoint
    if (audienceMode === "group") {
      if (!selectedGroupId) { alert("Please select a group"); return; }
      if (selectedGroupMembers.length === 0) { alert("Please select at least one group member"); return; }
      setSending(true); setResult(null);
      try {
        const payload = { body, ...(blastImage ? { media_url: blastImage.url } : {}) };
        if (selectedGroupMembers.length < groupMembers.length) {
          payload.member_ids = selectedGroupMembers;
        }
        const r = await axios.post(`${API}/api/groups/${selectedGroupId}/blast`, payload);
        setResult(r.data);
      } catch (e) {
        setResult({ error: e.response?.data?.detail || "Failed to send" });
      }
      setSending(false);
      return;
    }

    if (selected.length === 0) { alert("Please select at least one contact"); return; }

    if (scheduleMode) {
      if (!scheduleDate || !scheduleTime) { alert("Please select a date and time"); return; }
      const scheduledAt = new Date(`${scheduleDate}T${scheduleTime}:00`);
      if (scheduledAt <= new Date()) { alert("Scheduled time must be in the future"); return; }
    }

    setSending(true); setResult(null); setBlastProgress(null);
    try {
      const payload = { contact_ids: selected, body };
      if (blastImage) payload.media_url = blastImage.url;
      if (autoReplyEnabled) {
        payload.auto_reply_enabled = true;
        payload.auto_reply_message = autoReplyMessage;
      }
      if (scheduleMode) {
        payload.scheduled_at = `${scheduleDate}T${scheduleTime}:00`;
      }
      const r = await axios.post(`${API}/api/blasts`, payload);

      if (r.data.scheduled) {
        setResult(r.data);
        setSelected([]);
        setScheduleMode(false);
        setScheduleDate("");
        setScheduleTime("");
        loadScheduled();
        setSending(false);
        return;
      }

      // Background blast — poll for progress
      const blastId = r.data.blast_id;
      setBlastProgress({ processed: 0, sendable: r.data.sendable, sent: 0, failed: 0, status: "sending" });

      const poll = setInterval(async () => {
        try {
          const s = await axios.get(`${API}/api/blasts/${blastId}/status`);
          const d = s.data;
          setBlastProgress({ processed: d.processed, sendable: d.sendable, sent: d.sent, failed: d.failed, status: d.status });
          if (d.status === "sent" || d.status === "failed") {
            clearInterval(poll);
            setResult({ sent: d.sent, failed: d.failed, opted_out: d.opted_out, total: d.total, mock: d.mock, blast_id: blastId, auto_reply_enabled: autoReplyEnabled });
            setBlastProgress(null);
            setSending(false);
            setSelected([]);
          }
        } catch (_) {}
      }, 2000);
    } catch (e) {
      alert(e.response?.data?.detail || "Send failed");
      setSending(false);
    }
  };

  // Minimum date for the picker is today
  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="p-4 md:p-8" data-testid="blast-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold font-heading text-slate-900">Mass Blast</h1>
          <p className="text-slate-500 text-xs md:text-sm mt-0.5">Send SMS to multiple contacts at once</p>
        </div>
      </div>

      {/* Result banner */}
      {result && (
        <div className={`mb-6 card p-5 ${result.scheduled ? "border-amber-200 bg-amber-50" : "border-green-200 bg-green-50"}`} data-testid="blast-result">
          <div className="flex items-start justify-between">
            <div>
              {result.scheduled ? (
                <>
                  <p className="font-medium text-amber-800 font-heading flex items-center gap-2">
                    <Clock size={15} /> Blast scheduled!
                  </p>
                  <div className="flex gap-4 mt-2 text-sm text-amber-700">
                    <span>Recipients: <strong>{result.total}</strong></span>
                    <span>Sends at: <strong>{new Date(result.scheduled_at).toLocaleString()}</strong></span>
                  </div>
                </>
              ) : (
                <>
                  <p className="font-medium text-green-800 font-heading">Blast {result.mock ? "simulated" : "sent"} successfully!</p>
                  <div className="flex gap-4 mt-2 text-sm text-green-700">
                    <span>Sent: <strong>{result.sent}</strong></span>
                    <span>Failed: <strong>{result.failed}</strong></span>
                    <span>Opted Out (skipped): <strong>{result.opted_out}</strong></span>
                  </div>
                  {result.mock && <p className="text-xs text-green-600 mt-1">Simulation mode — add SignalWire credentials in Settings to send real SMS</p>}
                  {result.auto_reply_enabled && result.blast_id && (
                    <button
                      onClick={() => setReportBlastId(result.blast_id)}
                      className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-green-300 text-green-800 text-xs font-semibold rounded-md hover:bg-green-50 transition-colors"
                      data-testid="view-ai-report-btn"
                    >
                      <BarChart3 size={12} /> View AI Reply Report
                    </button>
                  )}
                </>
              )}
            </div>
            <button onClick={() => setResult(null)} className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
          </div>
        </div>
      )}

      {/* Scheduled Blasts */}
      <ScheduledBlastsList blasts={scheduledBlasts} onCancel={cancelScheduled} onRefresh={loadScheduled} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Left: Contacts */}
        <div className="card overflow-hidden" data-testid="contact-selector">
          <div className="p-4 border-b border-slate-100">
            {/* Audience mode toggle */}
            <div className="flex gap-1 mb-3 bg-slate-100 rounded-lg p-0.5">
              <button
                onClick={() => { setAudienceMode("synced"); setSelected([]); }}
                className={`flex-1 text-xs font-semibold py-1.5 rounded-md transition-colors ${audienceMode === "synced" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
              >Contacts</button>
              <button
                onClick={() => { setAudienceMode("smartlist"); setSelected([]); setContacts([]); }}
                className={`flex-1 text-xs font-semibold py-1.5 rounded-md transition-colors ${audienceMode === "smartlist" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
              >Smart List</button>
              <button
                onClick={() => { setAudienceMode("group"); setSelected([]); setContacts([]); setGroupMembers([]); setSelectedGroupMembers([]); setSelectedGroupId(""); }}
                className={`flex-1 text-xs font-semibold py-1.5 rounded-md transition-colors ${audienceMode === "group" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
              >Group</button>
            </div>

            {audienceMode === "group" ? (
              <div className="space-y-3">
                <p className="text-sm font-semibold text-slate-800">
                  <FolderOpen size={14} className="inline mr-1.5 text-slate-500" />
                  Send to Group
                </p>
                <select
                  value={selectedGroupId}
                  onChange={e => setSelectedGroupId(e.target.value)}
                  className="input text-xs py-1.5 w-full"
                >
                  <option value="">Select a group...</option>
                  {groups.map(g => <option key={g.id} value={g.id}>{g.name}{g.location ? ` — ${g.location}` : ""} ({g.member_count || 0})</option>)}
                </select>
                <button
                  onClick={loadGroupMembers}
                  disabled={!selectedGroupId || loadingGroup}
                  className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-[#FF5C35] text-white text-xs font-semibold rounded-md hover:bg-[#E84E29] transition-colors disabled:opacity-50"
                >
                  {loadingGroup ? "Loading..." : "Load Members"}
                </button>
                {groupMembers.length > 0 && (
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-xs text-slate-500">
                      {selectedGroupMembers.length > 0 ? <span className="text-[#FF5C35] font-semibold">{selectedGroupMembers.length} of {groupMembers.length} selected</span> : `${groupMembers.length} members loaded`}
                    </p>
                    <div className="flex gap-2">
                      <button onClick={selectAllGroupMembers} className="inline-flex items-center gap-1 px-2 py-1 bg-[#FF5C35] text-white text-[10px] font-semibold rounded-md hover:bg-[#E84E29] transition-colors">
                        <CheckSquare size={10} /> All ({groupMembers.length})
                      </button>
                      <button onClick={clearAllGroupMembers} className="inline-flex items-center gap-1 px-2 py-1 bg-slate-200 text-slate-700 text-[10px] font-semibold rounded-md hover:bg-slate-300 transition-colors">
                        <X size={10} /> Clear
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : audienceMode === "synced" ? (
              <>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <p className="text-sm font-semibold text-slate-800">
                    <Users size={14} className="inline mr-1.5 text-slate-500" />
                    <span data-testid="selection-count">
                      {selected.length > 0 ? <span className="text-[#FF5C35]">{selected.length} selected</span> : "Recipients"}
                    </span>
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={selectAll}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-[#FF5C35] text-white text-xs font-semibold rounded-md hover:bg-[#E84E29] transition-colors"
                      data-testid="select-all-btn"
                    >
                      <CheckSquare size={12} />
                      <span className="hidden sm:inline">Select All</span> ({contacts.length})
                    </button>
                    <button
                      onClick={clearAll}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-200 text-slate-700 text-xs font-semibold rounded-md hover:bg-slate-300 transition-colors"
                      data-testid="clear-selection-btn"
                    >
                      <X size={12} />
                      Clear
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <div className="relative flex-1">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input value={search} onChange={e => setSearch(e.target.value)} className="input pl-8 text-xs py-1.5" placeholder="Search..." data-testid="blast-search" />
                  </div>
                  <select value={filterStage} onChange={e => setFilterStage(e.target.value)} className="input w-auto text-xs py-1.5" data-testid="blast-filter-stage">
                    <option value="">All Stages</option>
                    {stages.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <select value={filterTag} onChange={e => setFilterTag(e.target.value)} className="input w-auto text-xs py-1.5" data-testid="blast-filter-tag">
                    <option value="">All Tags</option>
                    {tags.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </>
            ) : (
              <div className="space-y-3">
                <p className="text-sm font-semibold text-slate-800">
                  <Users size={14} className="inline mr-1.5 text-slate-500" />
                  FUB Smart List / Pond
                </p>
                <select
                  value={selectedList}
                  onChange={e => setSelectedList(e.target.value)}
                  className="input text-xs py-1.5 w-full"
                >
                  <option value="">Select a Smart List or Pond...</option>
                  {smartLists.length > 0 && <optgroup label="Smart Lists">
                    {smartLists.map(l => <option key={`sl-${l.id}`} value={`smartlist:${l.id}`}>{l.name}</option>)}
                  </optgroup>}
                  {ponds.length > 0 && <optgroup label="Ponds">
                    {ponds.map(p => <option key={`p-${p.id}`} value={`pond:${p.id}`}>{p.name}</option>)}
                  </optgroup>}
                </select>
                <button
                  onClick={loadListContacts}
                  disabled={!selectedList || loadingSmartList}
                  className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-[#FF5C35] text-white text-xs font-semibold rounded-md hover:bg-[#E84E29] transition-colors disabled:opacity-50"
                >
                  {loadingSmartList ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                      Pulling from FUB... (large lists may take a minute)
                    </span>
                  ) : "Load Contacts"}
                </button>
                {contacts.length > 0 && (
                  <p className="text-xs text-slate-500 text-center">
                    {contacts.length.toLocaleString()} contacts loaded{totalInFub && totalInFub > contacts.length ? ` of ${totalInFub.toLocaleString()} in FUB (API limit)` : ""} — all selected
                  </p>
                )}
              </div>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
            {audienceMode === "group" ? (
              loadingGroup ? (
                <div className="text-center py-8 text-slate-400 text-sm">Loading group members...</div>
              ) : groupMembers.length === 0 ? (
                <div className="text-center py-8 text-slate-400 text-sm">
                  {selectedGroupId ? "Select a group and click Load Members" : "Select a group above"}
                </div>
              ) : groupMembers.map(m => (
                <div
                  key={m.id}
                  onClick={() => toggleGroupMember(m.id)}
                  className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors hover:bg-slate-50 ${selectedGroupMembers.includes(m.id) ? "bg-[#FFF0EB]" : ""}`}
                >
                  {selectedGroupMembers.includes(m.id) ? <CheckSquare size={15} className="text-[#FF5C35] shrink-0" /> : <Square size={15} className="text-slate-300 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{m.first_name} {m.last_name}</p>
                    <p className="text-xs text-slate-500 truncate">{m.phone}</p>
                  </div>
                </div>
              ))
            ) : loading ? (
              <div className="text-center py-8 text-slate-400 text-sm">Loading...</div>
            ) : contacts.length === 0 ? (
              <div className="text-center py-8 text-slate-400 text-sm">No active contacts</div>
            ) : contacts.map(c => (
              <div
                key={c.id}
                onClick={() => toggleSelect(c.id)}
                className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors hover:bg-slate-50 ${selected.includes(c.id) ? "bg-[#FFF0EB]" : ""}`}
                data-testid={`contact-item-${c.id}`}
              >
                {selected.includes(c.id) ? <CheckSquare size={15} className="text-[#FF5C35] shrink-0" /> : <Square size={15} className="text-slate-300 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{c.first_name} {c.last_name}</p>
                  <p className="text-xs text-slate-500 truncate">{c.phone} • {c.stage}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Compose */}
        <div className="space-y-4">
          <div className="card p-5" data-testid="message-composer">
            <label className="block text-sm font-medium text-slate-700 mb-2">Message</label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {MERGE_FIELDS.map(f => (
                <button
                  key={f}
                  onClick={() => setBody(b => b + f)}
                  className="text-xs px-2.5 py-1 bg-[#FFF0EB] text-[#0F1419] border border-[#D1D5DB] rounded-full hover:bg-[#FFF0EB] transition-colors"
                  data-testid={`merge-field-${f}`}
                >
                  {f}
                </button>
              ))}
            </div>
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              className="input resize-none text-sm"
              rows={5}
              placeholder="Hi {first_name}, this is {agent_name} from ABC Realty..."
              data-testid="blast-message-input"
            />
            <SmsIndicator text={body} />

            {/* Image attachment */}
            <input type="file" ref={blastFileRef} accept="image/jpeg,image/png,image/gif,image/webp" className="hidden" onChange={handleBlastFileUpload} />
            <div className="mt-3 pt-3 border-t border-slate-100">
              {blastImage ? (
                <div className="flex items-center gap-3 bg-slate-50 rounded-lg p-2">
                  <img src={blastImage.preview} alt="" className="w-16 h-16 object-cover rounded-md" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-700 truncate">{blastImage.filename}</p>
                    <p className="text-[10px] text-slate-400">Will send as MMS to all recipients</p>
                  </div>
                  <button onClick={removeBlastImage} className="text-slate-400 hover:text-red-500 shrink-0"><X size={16} /></button>
                </div>
              ) : showBlastUrlInput ? (
                <div className="flex gap-2 items-center">
                  <input
                    value={blastImageUrlInput}
                    onChange={e => setBlastImageUrlInput(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleBlastUrlUpload(); }}}
                    className="input flex-1 text-xs py-1.5"
                    placeholder="Paste image URL..."
                    autoFocus
                  />
                  <button onClick={handleBlastUrlUpload} disabled={blastUploading || !blastImageUrlInput.trim()} className="btn-primary text-xs px-3 py-1.5 disabled:opacity-50">
                    {blastUploading ? "..." : "Add"}
                  </button>
                  <button onClick={() => { setShowBlastUrlInput(false); setBlastImageUrlInput(""); }} className="text-slate-400 hover:text-slate-600"><X size={14} /></button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={() => blastFileRef.current?.click()}
                    disabled={blastUploading}
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-slate-200 rounded-md text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50"
                  >
                    {blastUploading ? <div className="w-3 h-3 border-2 border-[#FF5C35] border-t-transparent rounded-full animate-spin" /> : <ImagePlus size={13} />}
                    Attach Photo
                  </button>
                  <button
                    onClick={() => setShowBlastUrlInput(true)}
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-slate-200 rounded-md text-slate-600 hover:bg-slate-50 transition-colors"
                  >
                    <LinkIcon size={13} /> Paste URL
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Schedule Toggle */}
          <div className="card p-5" data-testid="schedule-section">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-slate-700">Delivery</p>
              <div className="flex bg-slate-100 rounded-lg p-0.5" data-testid="schedule-toggle">
                <button
                  onClick={() => setScheduleMode(false)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${!scheduleMode ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                  data-testid="send-now-toggle"
                >
                  <Send size={11} className="inline mr-1" />
                  Send Now
                </button>
                <button
                  onClick={toggleScheduleMode}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${scheduleMode ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                  data-testid="schedule-toggle-btn"
                >
                  <Clock size={11} className="inline mr-1" />
                  Schedule
                </button>
              </div>
            </div>

            {scheduleMode && (
              <div className="space-y-3 pt-2" data-testid="schedule-picker">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Date</label>
                    <input
                      type="date"
                      value={scheduleDate}
                      onChange={e => setScheduleDate(e.target.value)}
                      min={today}
                      className="input text-sm"
                      data-testid="schedule-date"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Time</label>
                    <input
                      type="time"
                      value={scheduleTime}
                      onChange={e => setScheduleTime(e.target.value)}
                      className="input text-sm"
                      data-testid="schedule-time"
                    />
                  </div>
                </div>
                {scheduleDate && scheduleTime && (
                  <p className="text-xs text-amber-600 flex items-center gap-1.5 bg-amber-50 px-3 py-2 rounded-md">
                    <Clock size={11} />
                    Will send on {new Date(`${scheduleDate}T${scheduleTime}`).toLocaleString("en-US", { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" })}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* AI Auto-Reply */}
          <div className="card p-5" data-testid="auto-reply-section">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Bot size={14} className="text-[#FF5C35]" />
                <p className="text-sm font-medium text-slate-700">AI Auto-Reply</p>
              </div>
              <button
                onClick={() => setAutoReplyEnabled(!autoReplyEnabled)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${autoReplyEnabled ? "bg-[#FF5C35]" : "bg-slate-300"}`}
                data-testid="auto-reply-toggle"
              >
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${autoReplyEnabled ? "translate-x-4" : "translate-x-0.5"}`} />
              </button>
            </div>
            {autoReplyEnabled && (
              <div className="space-y-3">
                <p className="text-xs text-slate-500">Claude AI will classify incoming replies and auto-respond to confirmed leads. Requires an Anthropic API key in Settings.</p>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Auto-reply message for confirmed leads</label>
                  <textarea
                    value={autoReplyMessage}
                    onChange={e => setAutoReplyMessage(e.target.value)}
                    className="input resize-none text-sm"
                    rows={3}
                    placeholder="Thanks {first_name}! Someone from our team will reach out shortly."
                    data-testid="auto-reply-message"
                  />
                  <p className="text-xs text-slate-400 mt-1">Supports {"{first_name}"} and {"{last_name}"} merge fields. Sent only to replies classified as "confirmed interest."</p>
                </div>
                <div className="flex items-start gap-2 p-2.5 bg-[#FFF0EB] rounded-lg text-xs text-[#0F1419]">
                  <Info size={12} className="text-[#FF5C35] mt-0.5 shrink-0" />
                  <span>Replies are categorized as: <strong>Confirmed</strong>, <strong>Declined</strong>, <strong>Stop</strong>, <strong>Question</strong>, or <strong>Uncategorized</strong>. View the full report after replies come in.</span>
                </div>
              </div>
            )}
          </div>

          {/* Preview */}
          <div className="card p-5" data-testid="preview-section">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-slate-700">Preview</p>
              <button onClick={() => setPreview(!preview)} className="btn-secondary py-1 px-3 text-xs" data-testid="toggle-preview-btn">
                <Eye size={12} /> {preview ? "Hide" : "Show"} Preview
              </button>
            </div>
            {preview && contacts.find(c => selected[0] === c.id) && (
              <div className="bg-slate-900 rounded-xl p-4 text-sm text-white font-mono leading-relaxed">
                {previewMsg(contacts.find(c => selected[0] === c.id))}
                <span className="block mt-2 text-slate-400 text-xs">Reply STOP to unsubscribe</span>
              </div>
            )}
            {preview && !contacts.find(c => selected[0] === c.id) && (
              <p className="text-xs text-slate-400">Select a contact to preview</p>
            )}
          </div>

          <button
            onClick={sendBlast}
            disabled={sending || (audienceMode === "group" ? selectedGroupMembers.length === 0 : selected.length === 0) || !body.trim() || body.length > SMS_MAX || URL_PATTERN.test(body) || (scheduleMode && (!scheduleDate || !scheduleTime))}
            className={`w-full justify-center py-3 text-sm font-semibold rounded-lg transition-all flex items-center gap-2 ${
              scheduleMode
                ? "bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                : "btn-primary"
            }`}
            data-testid="send-blast-button"
          >
            {sending ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {scheduleMode ? "Scheduling..." : blastProgress ? `Sending... ${blastProgress.processed}/${blastProgress.sendable}` : "Sending..."}
              </span>
            ) : scheduleMode ? (
              <span className="flex items-center gap-2"><Clock size={15} />Schedule for {audienceMode === "group" ? selectedGroupMembers.length : selected.length} contact{(audienceMode === "group" ? selectedGroupMembers.length : selected.length) !== 1 ? "s" : ""}</span>
            ) : (
              <span className="flex items-center gap-2"><Send size={15} />Send to {audienceMode === "group" ? selectedGroupMembers.length : selected.length} contact{(audienceMode === "group" ? selectedGroupMembers.length : selected.length) !== 1 ? "s" : ""}</span>
            )}
          </button>
          {blastProgress && (
            <div className="mt-2 space-y-1">
              <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                <div className="h-full bg-orange-500 rounded-full transition-all duration-500" style={{ width: `${blastProgress.sendable > 0 ? (blastProgress.processed / blastProgress.sendable * 100) : 0}%` }} />
              </div>
              <p className="text-xs text-slate-500 text-center">
                {blastProgress.sent.toLocaleString()} sent{blastProgress.failed > 0 ? `, ${blastProgress.failed} failed` : ""} · {blastProgress.processed.toLocaleString()} of {blastProgress.sendable.toLocaleString()} processed
              </p>
            </div>
          )}
        </div>
      </div>

      {/* AI Reply Report Modal */}
      {reportBlastId && (
        <BlastReplyReport blastId={reportBlastId} onClose={() => setReportBlastId(null)} />
      )}
    </div>
  );
}
